/** @format */

import type { ContentDocument, ContentEditorPart, ContentMediaRef, ContentPost, ContentTag } from 'src/types';

export interface PreviewTopicMeta {
  id: string | number;
  name: string;
  displayName?: string | null;
}

export interface EditorPreviewInput {
  title: string;
  releaseDate: string | null;
  parts: ContentEditorPart[];
  selectedTags: Array<{ id: string | number; label?: string; name?: string }>;
  topicIds: Array<string | number>;
  topicMetaById?: Map<string, PreviewTopicMeta> | null;
}

export interface EditorPreviewResult {
  detail: ContentPost;
  /** Blob URLs created for local files; revoke them when the preview closes. */
  objectUrls: string[];
}

function createObjectUrl(file: unknown, objectUrls: string[]): string | null {
  if (typeof File === 'undefined' || !(file instanceof File)) return null;
  const url = URL.createObjectURL(file);
  objectUrls.push(url);
  return url;
}

function resolveMedia(
  media: ContentMediaRef | null | undefined,
  file: unknown,
  objectUrls: string[]
): ContentMediaRef | null {
  if (media && typeof media === 'object' && media.url) return { ...media };
  const url = createObjectUrl(file, objectUrls);
  if (!url) return media && typeof media === 'object' ? { ...media } : null;
  return { ...(media && typeof media === 'object' ? media : {}), url };
}

function resolveDocument(doc: ContentDocument, objectUrls: string[]): ContentDocument | null {
  if (!doc || typeof doc !== 'object') return null;
  if (doc._remove) return null;
  const url = doc.url || doc.remoteUrl || createObjectUrl(doc.file, objectUrls);
  if (!url) return null;
  return {
    ...doc,
    url,
    mimeType: doc.mimeType || (doc.file instanceof File ? doc.file.type : undefined)
  };
}

/**
 * Builds a ContentPost-like detail from the unsaved content editor draft so it can
 * be rendered by ExtranetContentBody without saving. Local files (images, videos,
 * documents) are exposed through blob URLs returned in `objectUrls`.
 */
export function mapEditorStateToPreviewDetail({
  title,
  releaseDate,
  parts,
  selectedTags,
  topicIds,
  topicMetaById
}: EditorPreviewInput): EditorPreviewResult {
  const objectUrls: string[] = [];

  const contentParts = (Array.isArray(parts) ? parts : [])
    .map((part, index) => {
      if (!part || typeof part !== 'object') return null;
      const base = { id: part.id ?? `preview-part-${index}`, type: part.type, weight: index };
      const file = Array.isArray(part._file) ? part._file[0] : part._file;

      switch (part.type) {
        case 'content':
          return { ...base, text: typeof part.text === 'string' ? part.text : '' };
        case 'documents': {
          const documents = (Array.isArray(part.documents) ? part.documents : [])
            .map(doc => resolveDocument(doc, objectUrls))
            .filter(Boolean) as ContentDocument[];
          return { ...base, documents };
        }
        case 'image':
          return { ...base, image: resolveMedia(part.image, file, objectUrls) };
        case 'imageText':
          return {
            ...base,
            title: typeof part.title === 'string' ? part.title : '',
            text: typeof part.text === 'string' ? part.text : '',
            image: resolveMedia(part.image, file, objectUrls)
          };
        case 'video':
          return { ...base, video: resolveMedia(part.video, file, objectUrls) };
        case 'salesInformationNo':
          return { ...base, salesInformationNo: part.salesInformationNo ?? '' };
        case 'serviceInformationNo':
          return { ...base, siNumber: part.siNumber ?? '' };
        case 'casaNumber':
          return {
            ...base,
            casaLinkActive: Boolean(part.casaLinkActive),
            casaPartNumbers: Array.isArray(part.casaPartNumbers) ? [...part.casaPartNumbers] : [],
            casaHierarchies: Array.isArray(part.casaHierarchies) ? [...part.casaHierarchies] : []
          };
        default:
          return null;
      }
    })
    .filter(Boolean) as ContentPost['contentParts'];

  const tags = (Array.isArray(selectedTags) ? selectedTags : [])
    .map(tag => {
      if (!tag || typeof tag !== 'object') return null;
      const name = tag.label || tag.name;
      return name ? { id: tag.id, name } : null;
    })
    .filter(Boolean) as ContentTag[];

  const topics = (Array.isArray(topicIds) ? topicIds : [])
    .map(topicId => topicMetaById?.get(String(topicId)))
    .filter((meta): meta is PreviewTopicMeta => Boolean(meta))
    .map(meta => ({ id: meta.id, name: meta.name, displayName: meta.displayName || meta.name }));

  const detail: ContentPost = {
    id: 'preview',
    title: typeof title === 'string' ? title : '',
    releaseDate: releaseDate || null,
    contentParts,
    tags,
    topics,
    text: '',
    pictureUrl: null,
    documents: []
  };

  return { detail, objectUrls };
}
